import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as historyService from '../services/history';
import * as localStorageActions from "./localStorage";

const initialState = {
  history: [],
  isFetching: false,
  isTotalFetching: false,
  error: null,
  isWrongActivationsHistoryFetching: false,
  wrongActivationsHistory: [],
  wrongActivationsHistoryError: null,
  totalCount: 0,
  pagination: { page: 0, sizePerPage: 0 },
  isBackgroundRequestsEnabled: localStorageActions.getIsBackgroundRequestsEnabled(),
};

const sliceName = 'history';

export const fetchNextHistory = createAsyncThunk(
  `${sliceName}/fetchNext`,
  (args, thunkAPI) => {
    return historyService
      .getHistory(args.filters, args.sort, args.limit, args.offset)
      .catch((error) => {
        error.clientMessage = "Can't get history from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const fetchStartHistory = createAsyncThunk(
  `${sliceName}/fetchStart`,
  (args, thunkAPI) => {
    return historyService
      .getHistory(args.filters, args.sort, args.limit)
      .catch((error) => {
        error.clientMessage = "Can't get history from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const fetchHistoryTotal = createAsyncThunk(
  `${sliceName}/fetchTotal`,
  (args, thunkAPI) => {
    return historyService
      .getHistoryTotal(args.filters, args.sort, args.limit)
      .catch((error) => {
        error.clientMessage = "Can't get history total from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const fetchWrongActivationsHistory = createAsyncThunk(
  `${sliceName}/fetchWrongActivations`,
  (args, thunkAPI) => {
    const filters = {
      responseType: "Permanent activation success"
    }
    return historyService
      .getHistory(filters, args.sort, '18446744073709551615', undefined) //https://dev.mysql.com/doc/refman/8.0/en/select.html
      .then((history) => {
        return history.filter((i) => {
          const { deviceKeyList, device } = i;
          if (!device || !deviceKeyList) return false;

          const deviceKey = device.key;
          const [firstKeyFromList] = deviceKeyList.split(',');
          return deviceKey !== firstKeyFromList;
        });
      })
      .catch((error) => {
        error.clientMessage = "Can't get history from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

const history = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    setIsBackgroundRequestsEnabled: (state, action) => {
      state.isBackgroundRequestsEnabled = action.payload;
      localStorageActions.setIsBackgroundRequestsEnabled(action.payload);
    },
    setPagination: (state, action) => {
      state.pagination = action.payload;
    },
  },
  extraReducers: {
    [fetchHistoryTotal.fulfilled]: (state, action) => {
      state.totalCount = action.payload;
      state.isTotalFetching = false;
    },
    [fetchStartHistory.fulfilled]: (state, action) => {
      state.isFetching = false;
      state.history = [...action.payload];

      if (state.history.length < state.pagination.sizePerPage) {
        const { page, sizePerPage } = state.pagination;
        state.totalCount = (page * sizePerPage) - sizePerPage + state.history.length;
      }
    },
    [fetchNextHistory.fulfilled]: (state, action) => {
      const history = action.payload;
      state.isFetching = false;
      state.history = [...history];

      if (state.history.length < state.pagination.sizePerPage) {
        const { page, sizePerPage } = state.pagination;
        state.totalCount = (page * sizePerPage) - sizePerPage + state.history.length;
      }
    },
    [fetchWrongActivationsHistory.fulfilled]: (state, action) => {
      const filteredHistory = action.payload;
      state.isWrongActivationsHistoryFetching = false;
      state.wrongActivationsHistory = filteredHistory;
    },

    [fetchHistoryTotal.pending]: (state) => {
      state.isTotalFetching = true;
    },
    [fetchStartHistory.pending]: (state) => {
      state.isFetching = true;
      state.error = null;
      state.history = [];
    },
    [fetchNextHistory.pending]: (state) => {
      state.isFetching = true;
      state.error = null;
    },
    [fetchWrongActivationsHistory.pending]: (state) => {
      state.isWrongActivationsHistoryFetching = true;
      state.wrongActivationsHistoryError = null;
    },

    [fetchHistoryTotal.rejected]: (state, action) => {
      state.isTotalFetching = false;
      state.error = action.payload;
    },
    [fetchStartHistory.rejected]: (state, action) => {
      state.isFetching = false;
      state.error = action.payload;
    },
    [fetchNextHistory.rejected]: (state, action) => {
      state.isFetching = false;
      state.error = action.payload;
    },
    [fetchWrongActivationsHistory.rejected]: (state, action) => {
      state.isWrongActivationsHistoryFetching = false;
      state.wrongActivationsHistoryError = action.payload;
    },
  },
});

const { reducer, actions } = history;

export const setIsBackgroundRequestsEnabled = (value) => (dispatch) => {
  dispatch(actions.setIsBackgroundRequestsEnabled(value));
};

export const setPagination = (value) => (dispatch) => {
  dispatch(actions.setPagination(value));
};

export const historySelectors = {
  historyState: (state) => state.history,
}

export default reducer;



// WEBPACK FOOTER //
// ./src/store/historySlice.js